// Semana 3 do Ciclo 1 da Bcorp: a frase de posicionamento de cada segmento candidato, para a
// sessão de 90 min. Uma frase por segmento (editável), na mesma linha da tabela dos 4 filtros, com a
// marca de qual foi a escolhida (só uma). Modelo da Bcorp: "Pra [segmento] que [situação], a Hydrodata
// entrega [resultado] com [diferencial]." É a síntese da decisão, não o pitch.
import { useCallback, useEffect, useState } from 'react';
import { Alert, App as AntApp, Button, Input, Radio, Skeleton, Space, Table, Tag, Typography } from 'antd';
import { conteudoFrases, ehConflito, ehSemAcesso, erroDe, salvarLinhaFrase, type ConteudoFrases } from '../api';
import { fraseAlterada, type LinhaFrase } from './beachheadRegras';
import type { PropsPagina } from './tipos';

const { Text, Paragraph } = Typography;
const PEQUENO = { fontSize: 12 } as const;

export default function FrasePosicionamento({ tarefa, podeVer, podeEditar }: PropsPagina) {
  const { message } = AntApp.useApp();
  const [dados, setDados] = useState<ConteudoFrases | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [semAcesso, setSemAcesso] = useState(false);
  const [edicoes, setEdicoes] = useState<Record<string, string>>({});
  const [salvando, setSalvando] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      setDados(await conteudoFrases(tarefa.id));
      setErro(null);
    } catch (e) {
      if (ehSemAcesso(e)) setSemAcesso(true); else setErro(erroDe(e));
    }
  }, [tarefa.id]);

  useEffect(() => { if (podeVer) void carregar(); }, [podeVer, carregar]);

  if (!podeVer || semAcesso) {
    return <Alert type="warning" showIcon title="Sem acesso" description="Esta página lê os negócios do comercial e é só para quem pode ver o painel do comercial." />;
  }
  if (erro) return <Alert type="error" showIcon title="Não foi possível carregar as frases" description={erro} action={<Button size="small" onClick={() => void carregar()}>Tentar de novo</Button>} />;
  if (!dados) return <Skeleton active />;

  const descartar = (chave: string) => setEdicoes((e) => { const n = { ...e }; delete n[chave]; return n; });
  const trocarLinha = (nova: LinhaFrase) => setDados((d) => (d ? { ...d, linhas: d.linhas.map((x) => (x.chave === nova.chave ? nova : x)) } : d));

  const gravar = async (l: LinhaFrase, campos: { frase?: string | null; frase_escolhida?: boolean }) => {
    setSalvando(l.chave);
    try {
      const nova = await salvarLinhaFrase(tarefa.id, l.chave, campos, { revisao: l.revisao });
      descartar(l.chave);
      message.success('Frase gravada.');
      // marcar uma escolhida desmarca a anterior no servidor: recarrega tudo
      if (campos.frase_escolhida === true || !nova) await carregar(); else trocarLinha(nova);
    } catch (e) {
      if (ehConflito(e)) {
        message.warning('Outra pessoa mudou esta linha. Recarreguei a página; confira e grave de novo.');
        descartar(l.chave);
        await carregar();
      } else if (ehSemAcesso(e)) {
        setSemAcesso(true);
      } else {
        message.error(erroDe(e));
      }
    } finally {
      setSalvando(null);
    }
  };

  const salvarFrase = (l: LinhaFrase) => {
    const campos = fraseAlterada(l, edicoes[l.chave]);
    if (!campos) { descartar(l.chave); return; }
    if ((campos.frase || '').length > dados.limite_frase) { message.warning(`A frase passa de ${dados.limite_frase} letras.`); return; }
    void gravar(l, campos);
  };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'grid', gap: 4 }}>
        <Text>Uma frase por segmento, no modelo da Bcorp. Marque a escolhida depois da sessão de 90 minutos.</Text>
        <Text type="secondary" style={PEQUENO}>Modelo: {dados.modelo}</Text>
        <Text type="secondary" style={PEQUENO}>
          A frase é a síntese da decisão, não o pitch: ela guia o roteiro da reunião, as opções de escopo e a abertura da proposta.
          O diferencial precisa passar no teste "um concorrente assinaria?": prova nossa, não adjetivo.
        </Text>
      </div>

      {!podeEditar && <Alert type="info" showIcon title="Você pode ver as frases, mas não editar." />}
      {dados.escolhida && <Alert type="success" showIcon title={`Frase escolhida: ${dados.escolhida}.`} />}

      {!dados.linhas.length ? (
        <Alert type="info" showIcon title="Ainda não há segmento medido. A tabela lê os negócios decididos desde 2025." />
      ) : (
        <Table<LinhaFrase>
          size="small" pagination={false} rowKey="chave" dataSource={dados.linhas} scroll={{ x: 'max-content' }}
          onRow={(l) => ({ style: l.candidato ? undefined : { opacity: 0.6 } })}
          columns={[
            {
              title: 'Segmento', width: 210, fixed: 'left',
              render: (_, l) => (
                <div>
                  <div><Text strong>{l.segmento}</Text></div>
                  <Text type="secondary" style={PEQUENO}>
                    {l.soma != null ? `soma ${l.soma} na tabela dos 4 filtros` : 'sem as 4 notas'}{l.candidato ? '' : ' · fora da mesa'}
                  </Text>
                </div>
              ),
            },
            {
              title: 'Frase de posicionamento', width: 560,
              render: (_, l) => {
                const texto = l.chave in edicoes ? edicoes[l.chave] : (l.frase || '');
                return (
                  <Input.TextArea
                    autoSize={{ minRows: 2, maxRows: 6 }} maxLength={dados.limite_frase} showCount value={texto}
                    disabled={!podeEditar || salvando === l.chave} placeholder={dados.modelo}
                    onChange={(ev) => setEdicoes((e) => ({ ...e, [l.chave]: ev.target.value }))}
                  />
                );
              },
            },
            {
              title: 'Escolhida', width: 120,
              render: (_, l) => (
                <Radio
                  checked={l.frase_escolhida} disabled={!podeEditar || salvando === l.chave || !l.frase || l.chave in edicoes}
                  onChange={() => void gravar(l, { frase_escolhida: true })}
                >{l.frase_escolhida ? 'Sim' : 'Marcar'}</Radio>
              ),
            },
            {
              title: '', width: 170, fixed: 'right',
              render: (_, l) => {
                const mexeu = !!fraseAlterada(l, edicoes[l.chave]);
                if (podeEditar && mexeu) {
                  return (
                    <Space size={4}>
                      <Button size="small" type="primary" loading={salvando === l.chave} onClick={() => salvarFrase(l)}>Salvar</Button>
                      <Button size="small" disabled={salvando === l.chave} onClick={() => descartar(l.chave)}>Descartar</Button>
                    </Space>
                  );
                }
                return l.frase_escolhida ? <Tag color="green">Escolhida</Tag> : null;
              },
            },
          ]}
        />
      )}
      <Paragraph type="secondary" style={{ ...PEQUENO, marginBottom: 0 }}>
        A ordem é a da tabela dos 4 filtros. Segmento fora da mesa fica apagado.
      </Paragraph>
    </div>
  );
}
